import React from "react";

type SkeletonVariant = "environment" | "project" | "template";

interface CardGridSkeletonProps {
    variant: SkeletonVariant;
    count?: number;
}

const EnvironmentCardSkeleton = () => (
    <div className="card-base environment-card card-skeleton">
        <div className="card-header">
            <div className="skeleton-line skeleton-title" />
            <div className="skeleton-line skeleton-badge" />
        </div>
        <div className="skeleton-line skeleton-description" />
        <div className="skeleton-line skeleton-description-short" />
        <div className="card-meta">
            <div className="skeleton-line skeleton-meta" />
            <div className="skeleton-line skeleton-meta" />
        </div>
        <div className="skeleton-line skeleton-region" />
        <div className="card-spacer" />
        <div className="card-footer">
            <div className="skeleton-line skeleton-footer" />
        </div>
    </div>
);

const ProjectCardSkeleton = () => (
    <div className="card-base project-card card-skeleton">
        <div className="card-header">
            <div className="skeleton-line skeleton-title" />
            <div className="skeleton-line skeleton-settings" />
        </div>
        <div className="skeleton-line skeleton-description" />
        <div className="skeleton-line skeleton-description-short" />
        <div className="skeleton-line skeleton-envs" />
        <div className="card-spacer" />
        <div className="card-footer">
            <div className="skeleton-line skeleton-tag" />
        </div>
    </div>
);

const TemplateCardSkeleton = () => (
    <div className="template-card card-skeleton">
        <div className="template-card-header">
            <div className="skeleton-line skeleton-icon" style={{ width: 44, height: 44, borderRadius: 10 }} />
            <div className="skeleton-line skeleton-badge" style={{ width: 24, height: 24, borderRadius: 12 }} />
        </div>
        <div className="skeleton-line skeleton-title" style={{ width: '70%', marginTop: 12 }} />
        <div className="skeleton-line skeleton-description" style={{ marginTop: 8 }} />
        <div className="skeleton-line skeleton-description-short" />
        <div className="template-card-spacer" />
        <div className="template-card-footer">
            <div style={{ display: 'flex', gap: 8 }}>
                <div className="skeleton-line" style={{ width: 20, height: 20, borderRadius: 4 }} />
                <div className="skeleton-line" style={{ width: 20, height: 20, borderRadius: 4 }} />
            </div>
            <div className="skeleton-line" style={{ width: 80, height: 14 }} />
        </div>
    </div>
);

const CardGridSkeleton: React.FC<CardGridSkeletonProps> = ({ variant, count = 6 }) => {
    const getSkeletonComponent = () => {
        switch (variant) {
            case "environment":
                return EnvironmentCardSkeleton;
            case "project":
                return ProjectCardSkeleton;
            case "template":
                return TemplateCardSkeleton;
            default:
                return ProjectCardSkeleton;
        }
    };

    const getGridClass = () => {
        switch (variant) {
            case "environment":
                return "cards-grid environment-cards-grid";
            case "project":
                return "cards-grid project-cards-grid";
            case "template":
                return "templates-grid";
            default:
                return "cards-grid";
        }
    };

    const SkeletonComponent = getSkeletonComponent();
    const gridClass = getGridClass();

    return (
        <div className={gridClass}>
            {Array.from({ length: count }, (_, i) => (
                <SkeletonComponent key={i} />
            ))}
        </div>
    );
};

export default CardGridSkeleton;

